import type { Discipline } from "./calc.js";

/**
 * Discipline -> legs, and the per-leg defaults. Values come from `LEG_DEFAULTS`
 * and `SPORT_LEGS` in docs/handover/reference/mvp-index.html; the README calls
 * them "the agreed defaults", so they are data, not suggestions.
 */

export type Sport = "bike" | "run" | "triathlon" | "duathlon";
/** A leg *key* is not a discipline: duathlon runs twice, as `run` and `run2`. */
export type LegKey = "swim" | "bike" | "run" | "run2";

export interface LegDef {
  type: Discipline;
  /** i18n key for the leg name, e.g. "Laufen". */
  nameKey: "swim" | "bike" | "run";
  /** i18n key for the stage label, e.g. "Laufteil". */
  stageKey: "stageSwim" | "stageBike" | "stageRun";
  durMinMin: number;
  durMinMax: number;
}

export const LEG_DEFS: Record<LegKey, LegDef> = {
  swim: { type: "swim", nameKey: "swim", stageKey: "stageSwim", durMinMin: 28, durMinMax: 34 },
  bike: { type: "bike", nameKey: "bike", stageKey: "stageBike", durMinMin: 140, durMinMax: 160 },
  run: { type: "run", nameKey: "run", stageKey: "stageRun", durMinMin: 100, durMinMax: 110 },
  run2: { type: "run", nameKey: "run", stageKey: "stageRun", durMinMin: 45, durMinMax: 52 },
};

export const SPORT_LEGS: Record<Sport, LegKey[]> = {
  bike: ["bike"],
  run: ["run"],
  triathlon: ["swim", "bike", "run"],
  duathlon: ["run", "bike", "run2"],
};

export const LEG_ICON: Record<Discipline, string> = {
  swim: "waves",
  bike: "bike",
  run: "footprints",
};

/** Per-leg vessel defaults (FR-1.8). */
export const VESSEL_DEFAULTS = {
  bottleMl: 750,
  cupMl: 100,
  stations: 15,
} as const;

export const BOTTLE_CHOICES = [500, 600, 750, 1000] as const;

/** Bounds from FR-1.5 and FR-1.6. */
export const LIMITS = {
  weight: { min: 40, max: 130, step: 1 },
  duration: { min: 5, max: 900, step: 5 },
} as const;

/**
 * A leg is numbered only when its type repeats within the chosen discipline:
 * duathlon gets "Laufen 1" / "Laufen 2", triathlon just "Laufen" (FR-1.3).
 */
export function ordinal(order: LegKey[], index: number): string {
  const target = LEG_DEFS[order[index]!].type;
  const sameType = order.filter((key) => LEG_DEFS[key].type === target);
  if (sameType.length < 2) return "";
  const position = order
    .slice(0, index + 1)
    .filter((key) => LEG_DEFS[key].type === target).length;
  return ` ${position}`;
}
